/**
 * A photograph the nurse can actually inspect: pinch or scroll to zoom, twist to
 * rotate, drag to pan, double-tap to snap between fit and close-up — and, when
 * the extractor told us where on the photo this medication is, a focus mode that
 * frames that one bottle and blurs the rest of the bench away.
 *
 * Built on PanResponder and Animated rather than react-native-gesture-handler,
 * because the app does not depend on gesture-handler or reanimated and pulling
 * either into the monorepo for one viewer is a much larger decision than this
 * screen deserves. The cost is that pinch and twist are tracked by hand from the
 * two touch points; the benefit is that the same component works untouched in
 * the web build, where the label photos are most often reviewed on a laptop.
 *
 * ROTATION IS FREE FOR THE BLUR, AND ONLY FOR THE BLUR. The focus overlay is a
 * sibling of the photo INSIDE the transformed view, so it is expressed in the
 * photo's own coordinates and any transform carries both together — verified at
 * 0°, 90°, 37° and -124°, with the mask and the outline welded to the bottle at
 * every one. The Gaussian is isotropic too, so a rotated blur is
 * indistinguishable from an unrotated one. What rotation DOES cost is every
 * piece of framing arithmetic below: the fit scale, the pan clamp and the focus
 * centring all have to work off the rotated bounding box rather than the photo's
 * own width and height.
 *
 * Panning is clamped to that rotated box, so it can never be dragged off into
 * empty space — at fit scale there is nowhere to go and the gesture is a no-op
 * rather than a way to lose the picture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Animated, Image, PanResponder, Platform, View, StyleSheet } from "react-native";
import Svg, {
    Defs,
    FeGaussianBlur,
    Filter,
    G,
    Image as SvgImage,
    Mask,
    Path,
    Rect
} from "react-native-svg";

import {
    fittedScale as fittedScaleFor,
    rotatedExtent,
    rotatePoint,
    snapRotation,
    unwrap
} from "./zoom-geometry";

import { logRocketRedactProps } from "@/lib/logrocket";

const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.75;
const DOUBLE_TAP_MS = 280;

/**
 * Focus never magnifies past 2x — half the photo stays on screen either side of
 * the medication. Framing a small box exactly would blow a 200-pixel bottle up
 * to full width and hand the nurse a wall of interpolation; keeping half the
 * photo in view also keeps the neighbouring bottles readable enough for her to
 * see that the box landed on the right one.
 */
const FOCUS_MAX_SCALE = 2;
/** Breathing room around the box, so the bottle is framed rather than cropped. */
const FOCUS_BOX_PADDING = 1.35;
/**
 * How far the blur takes to reach full strength beyond the bounding box, as a
 * multiple of the box's shorter side, with absolute bounds so a tiny box does
 * not get an invisible ramp and a huge one does not fade across the whole photo.
 *
 * 1.8 is close to the practical ceiling, and the ceiling is the point worth
 * recording: past it the ramp no longer bottoms out inside the visible frame,
 * every neighbouring label stays legible, and the mode stops indicating
 * anything — which matters more since the outline was removed and the blur
 * became the ONLY thing saying which bottle this is.
 *
 * Two things that look like they would buy a gentler onset and do not, both
 * measured at the real focus zoom over visit 419877: an ease-in curve in place
 * of the smoothstep (it spends most of the ramp near zero, so the frame never
 * reaches full blur), and simply deepening the Gaussian (the mask alpha gates
 * it, so a bigger sigma barely shows through a low band).
 */
const FOCUS_FADE_SPREAD_RATIO = 1.8;
const FOCUS_FADE_SPREAD_MIN = 48;
const FOCUS_FADE_SPREAD_MAX = 320;

/**
 * How many bands the falloff is built from. The ramp is drawn as concentric
 * rounded rectangles rather than a gradient, so this is its resolution: too few
 * and the steps terrace visibly in a smooth area, and they are cheap.
 */
const FOCUS_FADE_BANDS = 48;

/**
 * Four corner ticks, viewfinder style, in place of the outline this used to
 * draw round the box.
 *
 * Something has to answer "which bottle", and since the outline was removed the
 * blur alone does not: at a ramp this gentle the neighbouring labels stay
 * readable, and a nurse glancing at the photo cannot tell which one the card is
 * about. Ticks answer it instantly without enclosing the medication in a line,
 * so — unlike an outline — they add no edge of their own for the eye to catch,
 * and the falloff stays as soft as it is.
 *
 * Neutral white rather than brand teal: the point is to frame, not to colour.
 */
const FOCUS_TICK_LENGTH_RATIO = 0.25;
const FOCUS_TICK_LENGTH_MIN = 14;
const FOCUS_TICK_LENGTH_MAX = 34;
/** Drawn just clear of the box so they frame the item instead of touching it. */
const FOCUS_TICK_GAP = 4;
const FOCUS_TICK_WIDTH = 2.5;
const FOCUS_TICK_OPACITY = 0.8;

/** How much the surroundings are darkened, on top of being blurred. */
const FOCUS_SCRIM_OPACITY = 0.3;

/**
 * Gaussian radius, as a fraction of the viewport's shorter side. Carries more of
 * the work now that the ramp is longer: a gentler mask needs a deeper blur
 * underneath it to still read at the frame edges.
 */
const FOCUS_BLUR_RATIO = 0.03;

export interface FocusBox {
    /** All four are fractions of the image itself, 0..1, origin top-left. */
    x: number;
    y: number;
    width: number;
    height: number;
}

interface Props {
    uri: string;
    /** Viewport the photo is displayed in; panning is clamped against it. */
    width: number;
    height: number;
    accessibilityLabel?: string;
    /** Told the current scale so the caller can show a reset affordance. */
    onScaleChange?: (scale: number) => void;
    /** Where this medication sits on this photo, if the extractor could place it. */
    focusBox?: FocusBox | null;
    /** Whether the nurse has the focus magnifier switched on. */
    focusActive?: boolean;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function touchDistance(touches: { pageX: number; pageY: number }[]): number {
    const [a, b] = touches;
    return Math.hypot(a.pageX - b.pageX, a.pageY - b.pageY);
}

/** The angle of the line between two fingers. Wraps at ±π; callers must unwrap. */
function touchAngle(touches: { pageX: number; pageY: number }[]): number {
    const [a, b] = touches;
    return Math.atan2(b.pageY - a.pageY, b.pageX - a.pageX);
}

/** The transform the photo is under. `rotation` is radians, clockwise. */
interface Transform {
    scale: number;
    x: number;
    y: number;
    rotation: number;
}

export function ZoomableImage({
    uri,
    width,
    height,
    accessibilityLabel,
    onScaleChange,
    focusBox,
    focusActive = false
}: Props) {
    const scale = useRef(new Animated.Value(1)).current;
    const translateX = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(0)).current;
    // Held in degrees because that is what the transform wants; the maths below
    // works in radians. Interpolated rather than used raw so the native driver
    // can drive it, which a bare string transform cannot be.
    const rotationDeg = useRef(new Animated.Value(0)).current;
    const rotate = useMemo(
        () =>
            rotationDeg.interpolate({
                inputRange: [-360, 360],
                outputRange: ["-360deg", "360deg"]
            }),
        [rotationDeg]
    );

    // Plain mirrors of the animated values. Gesture maths needs to READ the
    // current transform, and Animated.Value has no synchronous getter that is
    // safe to rely on mid-gesture.
    const state = useRef<Transform>({ scale: 1, x: 0, y: 0, rotation: 0 });
    const gestureStart = useRef({ scale: 1, x: 0, y: 0, rotation: 0, distance: 0, angle: 0 });
    // Twist is accumulated frame by frame rather than measured against the start
    // of the gesture, so a turn of more than half a circle keeps going instead of
    // wrapping round and reversing.
    const lastTouchAngle = useRef(0);
    const twist = useRef(0);
    const lastTapAt = useRef(0);
    const [displayScale, setDisplayScale] = useState(1);
    const [displayRotation, setDisplayRotation] = useState(0);
    const containerRef = useRef<View | null>(null);

    // The photo is drawn `contain`ed, so unless its aspect ratio happens to match
    // the viewport it does NOT fill it — and a box expressed as a fraction of the
    // IMAGE cannot be placed without knowing the letterboxed rectangle the image
    // actually occupies. Null until the natural size is known; focus mode simply
    // does not engage before then.
    const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
    // `Image.getSize` resolves from the cache before the <Image> below has
    // painted, so without this the mask and the outline draw over a photo that
    // is not on screen yet — a teal rectangle floating on black for about a
    // second every time the viewer opens.
    const [painted, setPainted] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setNaturalSize(null);
        setPainted(false);
        Image.getSize(
            uri,
            (naturalWidth, naturalHeight) => {
                if (!cancelled && naturalWidth > 0 && naturalHeight > 0) {
                    setNaturalSize({ width: naturalWidth, height: naturalHeight });
                }
            },
            () => {
                /* Focus stays unavailable; pan and zoom are unaffected. */
            }
        );
        return () => {
            cancelled = true;
        };
    }, [uri]);

    /** The rectangle, in viewport coordinates at scale 1, the photo is drawn in. */
    const drawn = useMemo(() => {
        if (!naturalSize) {
            return null;
        }
        const fit = Math.min(width / naturalSize.width, height / naturalSize.height);
        const drawnWidth = naturalSize.width * fit;
        const drawnHeight = naturalSize.height * fit;
        return {
            width: drawnWidth,
            height: drawnHeight,
            left: (width - drawnWidth) / 2,
            top: (height - drawnHeight) / 2
        };
    }, [height, naturalSize, width]);

    /** The same box, in viewport coordinates at scale 1. */
    const boxRect = useMemo(() => {
        if (!drawn || !focusBox) {
            return null;
        }
        return {
            left: drawn.left + focusBox.x * drawn.width,
            top: drawn.top + focusBox.y * drawn.height,
            width: Math.max(1, focusBox.width * drawn.width),
            height: Math.max(1, focusBox.height * drawn.height)
        };
    }, [drawn, focusBox]);

    /**
     * The scale at which the photo, rotated by this much, exactly fits. Unrotated
     * that is 1 (the image is already `contain`ed); rotated it drops below 1,
     * which is why the zoom floor cannot be a constant.
     */
    const fittedScale = useCallback(
        (rotation: number) =>
            fittedScaleFor(drawn?.width ?? width, drawn?.height ?? height, width, height, rotation),
        [drawn, height, width]
    );

    const apply = useCallback(
        (next: Transform, animate = false) => {
            // Clamped against the rotated DRAWN photo, not the viewport: on a
            // portrait photo in a landscape viewport the letterbox bars are not
            // somewhere the nurse should be able to drag the picture into, and a
            // photo turned on its side occupies a different box entirely.
            const contentWidth = drawn?.width ?? width;
            const contentHeight = drawn?.height ?? height;
            const nextScale = clamp(
                next.scale,
                fittedScaleFor(contentWidth, contentHeight, width, height, next.rotation),
                MAX_SCALE
            );
            const extent = rotatedExtent(contentWidth, contentHeight, next.rotation);
            const slackX = Math.max(0, (extent.width * nextScale - width) / 2);
            const slackY = Math.max(0, (extent.height * nextScale - height) / 2);
            const resolved = {
                scale: nextScale,
                x: clamp(next.x, -slackX, slackX),
                y: clamp(next.y, -slackY, slackY),
                rotation: next.rotation
            };
            state.current = resolved;
            setDisplayScale(resolved.scale);
            setDisplayRotation(resolved.rotation);
            onScaleChange?.(resolved.scale);
            const degrees = (resolved.rotation * 180) / Math.PI;
            if (animate) {
                Animated.parallel([
                    Animated.spring(scale, {
                        toValue: resolved.scale,
                        useNativeDriver: true,
                        bounciness: 0
                    }),
                    Animated.spring(translateX, {
                        toValue: resolved.x,
                        useNativeDriver: true,
                        bounciness: 0
                    }),
                    Animated.spring(translateY, {
                        toValue: resolved.y,
                        useNativeDriver: true,
                        bounciness: 0
                    }),
                    Animated.spring(rotationDeg, {
                        toValue: degrees,
                        useNativeDriver: true,
                        bounciness: 0
                    })
                ]).start();
                return;
            }
            scale.setValue(resolved.scale);
            translateX.setValue(resolved.x);
            translateY.setValue(resolved.y);
            rotationDeg.setValue(degrees);
        },
        [drawn, height, onScaleChange, rotationDeg, scale, translateX, translateY, width]
    );

    // Back to the plain, square, fitted photo. Rotation goes too: this is the
    // escape hatch, and a "reset" that left the picture on its side would not be
    // one.
    const reset = useCallback(() => apply({ scale: 1, x: 0, y: 0, rotation: 0 }, true), [apply]);

    // A new photo starts fitted, otherwise the previous one's zoom is inherited.
    const resetForUri = useRef(uri);
    useEffect(() => {
        if (resetForUri.current === uri) {
            return;
        }
        resetForUri.current = uri;
        apply({ scale: 1, x: 0, y: 0, rotation: 0 });
    }, [apply, uri]);

    // Switching focus on frames the box; switching it off returns to the whole
    // photo. Deliberately NOT re-run on every pan: once framed, the nurse owns
    // the transform, and the highlight stays correct while she moves around
    // because the mask is drawn in the photo's own coordinates.
    const focusKey = `${uri}|${focusActive}|${boxRect ? `${boxRect.left},${boxRect.top}` : "none"}`;
    const lastFocusKey = useRef<string | null>(null);
    useEffect(() => {
        if (lastFocusKey.current === focusKey) {
            return;
        }
        lastFocusKey.current = focusKey;
        // Whatever the nurse has turned the photo to is hers to keep: switching
        // focus off means "show me the whole photo", not "undo my rotation".
        const rotation = state.current.rotation;
        if (!focusActive) {
            apply({ scale: fittedScale(rotation), x: 0, y: 0, rotation }, true);
            return;
        }
        if (!boxRect) {
            return;
        }
        // The box's own footprint once turned: a tall box laid on its side needs
        // the viewport's width, not its height.
        const boxExtent = rotatedExtent(boxRect.width, boxRect.height, rotation);
        const target = clamp(
            Math.min(
                width / (boxExtent.width * FOCUS_BOX_PADDING),
                height / (boxExtent.height * FOCUS_BOX_PADDING)
            ),
            fittedScale(rotation),
            FOCUS_MAX_SCALE
        );
        // The transform composes as translate · rotate · scale, so a point lands
        // at R(S(p)) + t. Centring the box therefore means undoing its offset
        // AFTER rotation — translating by the raw offset would send the photo off
        // at a tangent on anything but a square-on view.
        const offsetX = boxRect.left + boxRect.width / 2 - width / 2;
        const offsetY = boxRect.top + boxRect.height / 2 - height / 2;
        const rotated = rotatePoint(offsetX, offsetY, rotation);
        apply({ scale: target, x: -rotated.x * target, y: -rotated.y * target, rotation }, true);
    }, [apply, boxRect, fittedScale, focusActive, focusKey, height, width]);

    const responder = useMemo(
        () =>
            PanResponder.create({
                onStartShouldSetPanResponder: () => true,
                onMoveShouldSetPanResponder: (_event, gesture) =>
                    Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
                onPanResponderGrant: (event) => {
                    const touches = event.nativeEvent.touches ?? [];
                    const twoFingered = touches.length >= 2;
                    gestureStart.current = {
                        ...state.current,
                        distance: twoFingered ? touchDistance(touches) : 0,
                        angle: twoFingered ? touchAngle(touches) : 0
                    };
                    lastTouchAngle.current = gestureStart.current.angle;
                    twist.current = 0;

                    // Double-tap toggles between fitted and close-up, at whatever
                    // angle the photo is currently turned to.
                    const now = Date.now();
                    if (touches.length < 2 && now - lastTapAt.current < DOUBLE_TAP_MS) {
                        lastTapAt.current = 0;
                        const rotation = state.current.rotation;
                        apply(
                            state.current.scale > fittedScale(rotation) * 1.05
                                ? { scale: fittedScale(rotation), x: 0, y: 0, rotation }
                                : { scale: DOUBLE_TAP_SCALE, x: 0, y: 0, rotation },
                            true
                        );
                        return;
                    }
                    lastTapAt.current = now;
                },
                onPanResponderMove: (event, gesture) => {
                    const touches = event.nativeEvent.touches ?? [];
                    if (touches.length >= 2) {
                        // The second finger usually lands AFTER the gesture has
                        // begun. Seeding the baseline here rather than only in
                        // grant is what stops that whole gesture falling through
                        // to a one-finger pan.
                        if (gestureStart.current.distance === 0) {
                            gestureStart.current = {
                                ...state.current,
                                distance: touchDistance(touches),
                                angle: touchAngle(touches)
                            };
                            lastTouchAngle.current = gestureStart.current.angle;
                            twist.current = 0;
                        }
                        const ratio = touchDistance(touches) / gestureStart.current.distance;
                        const angle = touchAngle(touches);
                        twist.current += unwrap(angle - lastTouchAngle.current);
                        lastTouchAngle.current = angle;
                        apply({
                            scale: gestureStart.current.scale * ratio,
                            x: gestureStart.current.x,
                            y: gestureStart.current.y,
                            rotation: gestureStart.current.rotation + twist.current
                        });
                        return;
                    }
                    apply({
                        scale: gestureStart.current.scale,
                        x: gestureStart.current.x + gesture.dx,
                        y: gestureStart.current.y + gesture.dy,
                        rotation: state.current.rotation
                    });
                },
                onPanResponderRelease: () => {
                    // Magnet, not a ratchet — see `snapRotation`: only a twist
                    // that already landed near square is pulled the rest of the
                    // way, so a label photographed at an angle stays there.
                    const rotation = state.current.rotation;
                    const snapped = snapRotation(rotation);
                    if (Math.abs(snapped - rotation) < 1e-4) {
                        return;
                    }
                    // Snapping changes what "fitted" means. Re-fit only if she was
                    // not deliberately zoomed in, otherwise the snap would throw
                    // away a close-up she had just set up.
                    const wasFitted = state.current.scale <= fittedScale(rotation) * 1.02;
                    apply(
                        {
                            ...state.current,
                            rotation: snapped,
                            scale: wasFitted ? fittedScale(snapped) : state.current.scale
                        },
                        true
                    );
                },
                onPanResponderTerminationRequest: () => false
            }),
        [apply, fittedScale]
    );

    // Web: a trackpad or wheel is how anyone reviewing on a laptop will zoom, and
    // PanResponder never sees wheel events.
    useEffect(() => {
        if (Platform.OS !== "web") {
            return;
        }
        const node = containerRef.current as unknown as HTMLElement | null;
        if (!node?.addEventListener) {
            return;
        }
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const factor = Math.exp(-event.deltaY / 320);
            apply({
                scale: state.current.scale * factor,
                x: state.current.x,
                y: state.current.y,
                rotation: state.current.rotation
            });
        };
        node.addEventListener("wheel", onWheel, { passive: false });
        return () => node.removeEventListener("wheel", onWheel);
    }, [apply]);

    const showFocusOverlay = focusActive && painted && Boolean(drawn && boxRect);
    // Offered once the photo is no longer square-on and fitted — a rotation is
    // just as much a state that needs an undo as a zoom is.
    const showResetHit = (displayScale > 1.05 || displayRotation !== 0) && !focusActive;

    return (
        <View
            ref={containerRef}
            style={[styles.viewport, { width, height }]}
            {...responder.panHandlers}
            accessibilityRole="image"
            accessibilityLabel={accessibilityLabel}
            // Announced so the affordance is discoverable without a caption.
            accessibilityHint="Pinch or scroll to zoom, twist two fingers to rotate, drag to pan, double tap to reset"
        >
            <Animated.View
                // Order is load-bearing: listed this way the transforms compose
                // as translate · rotate · scale, which is what the focus centring
                // maths above assumes.
                style={{
                    transform: [{ translateX }, { translateY }, { rotate }, { scale }]
                }}
            >
                <Image
                    {...logRocketRedactProps()}
                    source={{ uri }}
                    style={{ width, height }}
                    resizeMode="contain"
                    onLoad={() => setPainted(true)}
                />
                {showFocusOverlay && drawn && boxRect && (
                    <FocusOverlay
                        uri={uri}
                        width={width}
                        height={height}
                        drawn={drawn}
                        boxRect={boxRect}
                    />
                )}
            </Animated.View>
            {showResetHit && (
                <View style={styles.resetHit} onTouchEnd={reset} pointerEvents="box-none" />
            )}
        </View>
    );
}

/**
 * The rest of the bench, blurred and dimmed away from the medication.
 *
 * Drawn as SVG rather than with a native blur view for one practical reason:
 * react-native-svg is already a dependency and carries real SVG filters, while
 * every blur package in this ecosystem is a NATIVE module — adding one would put
 * a fresh EAS build between this feature and the nurses instead of an OTA
 * update. It also gives a genuinely GRADUAL falloff, which stacked blur views
 * cannot do without a dozen overlapping layers.
 *
 * The layer sits INSIDE the transformed view, so it is expressed in the photo's
 * own coordinates and stays welded to the bottle through any pan or zoom.
 *
 * Where SVG filters are unavailable (older Android renderers) the Gaussian pass
 * is skipped by the platform and the layer degrades to the dimming scrim alone —
 * much reduced, and with the outline gone it is now the only fallback there is.
 */
function FocusOverlay({
    uri,
    width,
    height,
    drawn,
    boxRect
}: {
    uri: string;
    width: number;
    height: number;
    drawn: { width: number; height: number; left: number; top: number };
    boxRect: { left: number; top: number; width: number; height: number };
}) {
    const blurRadius = Math.max(4, Math.min(width, height) * FOCUS_BLUR_RATIO);
    const spread = clamp(
        Math.min(boxRect.width, boxRect.height) * FOCUS_FADE_SPREAD_RATIO,
        FOCUS_FADE_SPREAD_MIN,
        FOCUS_FADE_SPREAD_MAX
    );

    const tickLength = clamp(
        Math.min(boxRect.width, boxRect.height) * FOCUS_TICK_LENGTH_RATIO,
        FOCUS_TICK_LENGTH_MIN,
        FOCUS_TICK_LENGTH_MAX
    );
    const tickLeft = boxRect.left - FOCUS_TICK_GAP;
    const tickTop = boxRect.top - FOCUS_TICK_GAP;
    const tickRight = boxRect.left + boxRect.width + FOCUS_TICK_GAP;
    const tickBottom = boxRect.top + boxRect.height + FOCUS_TICK_GAP;
    const ticks = [
        `M${tickLeft},${tickTop + tickLength} L${tickLeft},${tickTop} L${tickLeft + tickLength},${tickTop}`,
        `M${tickRight - tickLength},${tickTop} L${tickRight},${tickTop} L${tickRight},${tickTop + tickLength}`,
        `M${tickRight},${tickBottom - tickLength} L${tickRight},${tickBottom} L${tickRight - tickLength},${tickBottom}`,
        `M${tickLeft + tickLength},${tickBottom} L${tickLeft},${tickBottom} L${tickLeft},${tickBottom - tickLength}`
    ];

    // The falloff, as concentric rounded rectangles from the outermost band
    // (white, full blur) inwards to the box outline itself (black, untouched).
    // Painted largest first so each one covers the last.
    //
    // Rectangles rather than the radial gradient this used to be, because a
    // gradient cannot start evenly at a RECTANGLE's outline: an ellipse through
    // the box's edge midpoints cuts its corners off, and one through its corners
    // leaves a halo at the edges. The old ellipse blurred the box's own corners
    // while its middle stayed sharp, and that inconsistency — not the fade
    // itself — is what read as a hard edge.
    const bands = Array.from({ length: FOCUS_FADE_BANDS }, (_, index) => {
        const outward = 1 - index / (FOCUS_FADE_BANDS - 1);
        // Eased rather than linear: a straight ramp has a kink in its slope at
        // each end, and the eye finds those kinks as faint rings.
        const eased = outward * outward * (3 - 2 * outward);
        const level = Math.round(eased * 255)
            .toString(16)
            .padStart(2, "0");
        const inset = spread * outward;
        return {
            key: index,
            x: boxRect.left - inset,
            y: boxRect.top - inset,
            width: boxRect.width + inset * 2,
            height: boxRect.height + inset * 2,
            radius: 6 + inset,
            fill: `#${level}${level}${level}`
        };
    });

    return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
            <Svg width={width} height={height}>
                <Defs>
                    {/* Generous filter region: a deeper Gaussian sampled over a
                        tight one leaves the photo's own edges visibly lighter. */}
                    <Filter id="focusBlur" x="-30%" y="-30%" width="160%" height="160%">
                        <FeGaussianBlur in="SourceGraphic" stdDeviation={blurRadius} />
                    </Filter>
                    {/* White shows the blurred copy, black lets the sharp photo
                        underneath through. So: white everywhere, then the bands
                        stepping down to black over the medication itself. */}
                    <Mask id="focusMask">
                        <Rect x={0} y={0} width={width} height={height} fill="#FFFFFF" />
                        {bands.map((band) => (
                            <Rect
                                key={band.key}
                                x={band.x}
                                y={band.y}
                                width={band.width}
                                height={band.height}
                                rx={band.radius}
                                ry={band.radius}
                                fill={band.fill}
                            />
                        ))}
                    </Mask>
                </Defs>
                <G mask="url(#focusMask)">
                    <SvgImage
                        href={{ uri }}
                        x={drawn.left}
                        y={drawn.top}
                        width={drawn.width}
                        height={drawn.height}
                        preserveAspectRatio="none"
                        filter="url(#focusBlur)"
                    />
                    <Rect
                        x={0}
                        y={0}
                        width={width}
                        height={height}
                        fill="#0B1219"
                        opacity={FOCUS_SCRIM_OPACITY}
                    />
                </G>
                {ticks.map((tick) => (
                    <Path
                        key={tick}
                        d={tick}
                        fill="none"
                        stroke="#FFFFFF"
                        strokeOpacity={FOCUS_TICK_OPACITY}
                        strokeWidth={FOCUS_TICK_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                ))}
            </Svg>
        </View>
    );
}

const styles = StyleSheet.create({
    viewport: {
        overflow: "hidden",
        alignItems: "center",
        justifyContent: "center",
        // Dark ground so a photo with white margins does not bleed into the sheet.
        backgroundColor: "#0F1720"
    },
    resetHit: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    }
});
